<template>
  <div class="export-receipt-detail print-section">
    <!-- Header Section -->
    <div class="header">
      <h1>PHIẾU XUẤT</h1>
      <div class="meta-info">
        (Mã phiếu <strong>PXK{{ receipt.ID }}</strong>) {{ warehouseName }}
        <span class="tag tag-gray">{{ getReceiptStatusName(receipt.Status) }}</span>
        <span :class="['tag', walletAfterExport >= 0 ? 'tag-blue' : 'tag-red']">{{ walletAfterExport >= 0 ? 'Đủ số dư' : 'Chưa thanh toán' }}</span>
      </div>
      <div style="margin-top: 5px;">
        Phương thức vận chuyển: 
        <span v-if="isReadonlyReceipt || receipt.ID === 0">{{ display_ship_method(receipt.ShippingMethod) }}</span>
        <select v-else v-model="localShippingMethod" class="input-inline">
          <option value="">-- Chọn kiểu ship --</option>
          <option v-for="method in SHIP_METHODS" :key="method.value" :value="method.name">
            {{ method.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Top Grid -->
    <div class="top-grid">
      <!-- Customer Info -->
      <div>
        <div class="section-title">Khách hàng</div>
        <table class="info-table">
          <tr>
            <td class="label-bg">Khách</td>
            <td>
              <span v-if="isReadonlyReceipt || receipt.ID === 0">{{ customerFullName }}</span>
              <input v-else type="text" v-model="localReceiver" class="input-table">
            </td>
          </tr>
          <tr>
            <td class="label-bg">Mã Khách</td>
            <td>{{ customer.UserCode || receipt.UserCode || '' }}</td>
          </tr>
          <tr>
            <td class="label-bg">Địa chỉ</td>
            <td>
              <span v-if="isReadonlyReceipt || receipt.ID === 0">{{ receipt.Address }}</span>
              <input v-else type="text" v-model="localAddress" class="input-table">
            </td>
          </tr>
          <tr v-if="!isReadonlyReceipt && receipt.ID > 0">
            <td class="label-bg">SĐT nhận</td>
            <td>
              <input type="text" v-model="localPhone" class="input-table">
            </td>
          </tr>
          <tr>
            <td class="label-bg">Thu tiền ship</td>
            <td>
              <span v-if="isReadonlyReceipt || receipt.ID === 0">{{ display_ship_payment_type(receipt.ShipPaymentType) }}</span>
              <select v-else v-model.number="localShipPaymentType" class="input-table">
                <option v-for="pt in SHIP_PAYMENT_TYPES" :key="pt.value" :value="pt.value">
                  {{ pt.name }}
                </option>
              </select>
            </td>
          </tr>
        </table>
      </div>

      <!-- Financial Info -->
      <div>
        <div class="section-title">Tiền cần thanh toán (đơn vị đ)</div>
        <table class="info-table">
          <tr>
            <td class="label-bg">a. Tiền cần thanh toán</td>
            <td style="text-align: right; font-weight: bold;">{{ totalAmount | currency }}</td>
          </tr>
          <tr>
            <td class="label-bg">b. Tiền ship hàng (khách trả)</td>
            <td style="text-align: right;">
              <span v-if="isExportedReceipt">{{ shipVN | currency }}</span>
              <h-currency-input v-else :value="shipVN" :decimal="0" showClass="compact-currency" :disabled="Number(localShipPaymentType) === 1" @input="setCustomerShipVN" />
            </td>
          </tr>
          <tr class="no-print">
            <td class="label-bg">Tiền ship hàng (cty trả)</td>
            <td style="text-align: right;">
              <span v-if="isExportedReceipt">{{ companyShipVN | currency }}</span>
              <h-currency-input v-else :value="companyShipVN" :decimal="0" showClass="compact-currency" @input="setCompanyShipVN" />
            </td>
          </tr>
          <tr>
            <td class="label-bg">c. Số dư hiện tại</td>
            <td style="text-align: right; color: green;" :style="{ color: customer.Wallet >= 0 ? 'green' : 'red' }">
              {{ (customer.Wallet || 0) | currency }}
            </td>
          </tr>
          <tr>
            <td class="label-bg"><strong>Số dư sau khi xuất kho (c - a - b)</strong></td>
            <td style="text-align: right;" :style="{ color: walletAfterExport >= 0 ? 'green' : 'red' }">
              <strong>{{ walletAfterExport | currency }}</strong>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <!-- Export Receipt Main Info -->
    <div class="section-title">Thông tin phiếu xuất</div>
    <table class="data-table">
      <thead>
        <tr>
          <th style="width: 50%; text-align: center; font-weight: bold;">1. Tổng cân nặng/ Tổng số khối/Tổng cân quy đổi</th>
          <th style="width: 50%; text-align: center; font-weight: bold;">2. Tổng số kiện</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="text-align: center;">{{ sumRawWeight | currency('', 2) }} KG / {{ sumVolume | currency('', 2) }} CBM / {{ sumWeightByVolume | currency('', 2) }} KG</td>
          <td style="text-align: center;">{{ sumNumberOfPack }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Packages Section -->
    <div class="section-title" style="margin-top: 25px;">Thông tin gói hàng</div>
    <table class="package-table">
      <thead>
        <tr>
          <th rowspan="2" style="width: 5%;">##</th>
          <th style="width: 10%;">Check mvđ</th>
          <th rowspan="2" style="width: 15%;">Mã vận đơn</th>
          <th rowspan="2" style="width: 10%;">Số kiện</th>
          <th rowspan="2" style="width: 10%;">Đơn hàng</th>
          <th rowspan="2" style="width: 15%;">Cân nặng/Số khối/Cân quy đổi</th>
          <th rowspan="2" style="width: 10%;">Cân tính tiền</th>
          <th rowspan="2" style="width: 15%;">Ghi chú</th>
          <th rowspan="2" style="width: 15%;" v-if="!isReadonlyReceipt">##</th>
        </tr>
        <tr>
          <th style="background: white; padding: 5px;" class="no-print">
            <input 
              v-if="!isReadonlyReceipt"
              type="text" 
              placeholder="Kiểm tra MVĐ" 
              class="input-mini"
              v-model="scanCodeHeader"
              @keyup.enter="handleHeaderScan"
            ><br v-if="!isReadonlyReceipt">
            <button v-if="!isReadonlyReceipt" class="btn-green-sm" style="margin-top: 3px;" @click="handleHeaderScan">Nhập</button>
          </th>
        </tr>
      </thead>
      <tbody v-for="group in groupedPackages" :key="'group-' + group.orderId">
        <!-- Package Rows -->
        <tr v-for="(p, idx) in group.packages" :key="'pkg-' + p.ID" :class="{ 'bill-checked': p.Matched }">
          <td v-if="idx === 0" :rowspan="group.rowspan">{{ group.stt }}</td>
          <td>
            <input type="checkbox" v-model="p.Matched" @change="updateMatchedCount">
          </td>
          
          <td><span class="text-blue">{{ p.TransactionCode }}</span></td>
          <td>{{ p.NumberOfPack || 0 }}</td>
          <td>
            <a target="_blank" :href="(p.PackageType === 1 ? '/ky-gui/' : '/don-hang/') + p.OrderID" class="text-blue">
              {{ (p.PackageType === 1 ? 'KG' : 'MD') + p.OrderID }}
            </a>
          </td>
          <td>
            {{ p.Weight | currency('', 2) }} /
            {{ p.Volume | currency('', 2) }}
            <span v-if="p.Length && p.Width && p.Height" rel="tooltip" :title="`${p.Length} x ${p.Width} x ${p.Height}`">(?)</span> /
            {{ p.WeightByVolume | currency('', 2) }}
          </td>
          <td>{{ getChargeableWeight(p) | currency('', 2) }}</td>
          <td>{{ p.OrderNote || p.StoreVNNote || '' }}</td>
          
          <td v-if="!isReadonlyReceipt">
            <button class="btn-delete" @click="confirmDeleteSinglePackage(p)">Xóa mã</button>
          </td>
        </tr>
        <!-- Order Subtotal Row -->
        <tr class="subtotal-row">
          <td colspan="3" style="text-align: right; border-top: none; font-weight: bold;">
            Tổng:
          </td>
          <td style="font-weight: bold;">{{ group.subtotalNumberOfPack }}</td>
          <td></td>
          <td style="font-weight: bold;">
            {{ group.subtotalWeight | currency('', 2) }} KG /
            {{ group.subtotalVolume | currency('', 2) }} CBM /
            {{ group.subtotalWeightByVolume | currency('', 2) }} KG
          </td>
          <td style="font-weight: bold;">{{ group.subtotalChargeableWeight | currency('', 2) }}</td>
          <td></td>
          <td v-if="!isReadonlyReceipt"></td>
        </tr>
      </tbody>
      <!-- If no packages -->
      <tbody v-if="!packages || packages.length === 0">
        <tr>
          <td :colspan="!isReadonlyReceipt ? 9 : 8" class="center">Không có gói hàng nào.</td>
        </tr>
      </tbody>
    </table>

    <div class="date-info">Ngày <strong>{{ receipt.CreatedDate | display_date('DD-MM-YYYY HH:mm:ss') }}</strong></div>

    <div class="signature-row">
      <span>Khách hàng</span>
      <span>Nhân viên xuất kho</span>
    </div>
    <div class="signature-names">
      <span>{{ customerFullName }}</span>
      <span>{{ receipt.CreatedByName || 'Kho NTG' }}</span>
    </div>

    <div style="height: 100px;"></div>

    <!-- Bottom Control Bar (Hidden on print) -->
    <div class="bottom-bar no-print">
      <div class="search-box">
        <input 
          v-if="!isReadonlyReceipt"
          type="text" 
          placeholder="Nhập mã vận đơn và bấm enter"
          v-model="scanCodeBottom"
          @keyup.enter="handleBottomScan"
        >
        <button v-if="!isReadonlyReceipt" class="btn-action btn-red" @click="handleBottomScan">Nhập</button>
        <button v-if="!isReadonlyReceipt" class="btn-action btn-green" @click="checkAllPackages">Nhập tất cả mã</button>
      </div>
      <div>
        <button class="btn-action" @click="$emit('close')">Quay lại</button>
        <button v-if="!isReadonlyReceipt && receipt.ID > 0" class="btn-action btn-green" @click="updateReceipt">Cập nhật</button>
        <button v-if="canExportReceipt && receipt.ID > 0" class="btn-action" @click="onExport">Xuất kho</button>
        <button v-if="canRedeliverReceipt && receipt.ID > 0" class="btn-action btn-warning" @click="redeliverReceipt">Giao lại</button>
        <button v-if="canShipReceipt && receipt.ID > 0" class="btn-action btn-blue" @click="shipReceipt">Đi ship</button>
        <button v-if="receipt.ID > 0" class="btn-action btn-dark-red" @click="printReceipt">In Phiếu</button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import configs from "@/configs";
import {
  calculateLiquidationAmount,
  getChargeableWeight,
  getPackageTotal
} from "./liquidation";

const EXPORT_RECEIPT_STATUS = {
  PREPARING: 'chuan_bi',
  WAITING_REDELIVERY: 'cho_giao_lai',
  SHIPPING: 'dang_giao',
  EXPORTED: 'da_xuat',
  CANCELED: 'da_huy'
};

const SHIP_PAYMENT_TYPES = [
  { value: 1, name: 'Free ship' },
  { value: 0, name: 'Trừ ví điện tử của khách' },
  { value: 2, name: 'Kho ship thu tiền mặt từ khách' },
  { value: 3, name: 'Shipper tự thu' }
];

const SHIP_METHODS = configs.SHIP_METHODS || [];

export default {
  name: "ExportReceiptPrint",
  props: {
    receipt: {
      type: Object,
      required: true
    },
    packages: {
      type: Array,
      required: true,
      default: () => []
    },
    warehouses: {
      type: Array,
      required: true
    },
    customers: {
      type: Array,
      required: true
    },
    canOperate: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      SHIP_METHODS: configs.SHIP_METHODS,
      SHIP_PAYMENT_TYPES,
      shipVN: this.receipt.ShipVN !== undefined ? this.receipt.ShipVN : null,
      companyShipVN: this.receipt.CompanyShipVN !== undefined ? this.receipt.CompanyShipVN : null,
      localPackages: [],
      scanCodeHeader: '',
      scanCodeBottom: '',
      localReceiver: this.receipt.Receiver || '',
      localPhone: this.receipt.Phone || '',
      localAddress: this.receipt.Address || '',
      localShippingMethod: this.receipt.ShippingMethod || '',
      localShipPaymentType: this.receipt.ShipPaymentType !== undefined && this.receipt.ShipPaymentType !== null ? this.receipt.ShipPaymentType : 0
    };
  },
  watch: {
    receipt: {
      handler(newReceipt) {
        this.shipVN = newReceipt.ShipVN !== undefined ? newReceipt.ShipVN : null;
        this.companyShipVN = newReceipt.CompanyShipVN !== undefined ? newReceipt.CompanyShipVN : null;
        this.localReceiver = newReceipt.Receiver || '';
        this.localPhone = newReceipt.Phone || '';
        this.localAddress = newReceipt.Address || '';
        this.localShippingMethod = newReceipt.ShippingMethod || '';
        this.localShipPaymentType = newReceipt.ShipPaymentType !== undefined && newReceipt.ShipPaymentType !== null ? newReceipt.ShipPaymentType : 0;
        if (Number(this.localShipPaymentType) === 1) {
          this.shipVN = 0;
        }
      },
      deep: true
    },
    localShipPaymentType: {
      handler(value) {
        if (Number(value) === 1) {
          this.shipVN = 0;
        }
      },
      immediate: true
    },
    packages: {
      handler(newPkgs) {
        this.localPackages = _.cloneDeep(newPkgs).map(p => ({
          ...p,
          Matched: p.Matched || false
        }));
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    customer() {
      const c = (this.customers || []).find(c => c.ID === this.receipt.UID);
      if (c) return c;
      return {
        Wallet: this.receipt.Wallet,
        FirstName: this.receipt.FirstName,
        LastName: this.receipt.LastName,
        Username: this.receipt.Username
      };
    },
    warehouseName() {
      if (this.customer && this.customer.ReceivePlaceID) {
        const wh = this.warehouses.find(w => w.ID === this.customer.ReceivePlaceID);
        return wh ? wh.Name : 'Hà Nội';
      }
      return 'Hà Nội';
    },
    customerFullName() {
      if (this.customer) {
        const firstName = this.customer.FirstName || '';
        const lastName = this.customer.LastName || '';
        return (lastName + ' ' + firstName).trim() || this.customer.Username || this.receipt.Username;
      }
      return this.receipt.Username;
    },
    totalWeight() {
      if (this.isReadonlyReceipt && this.receipt.Weight !== undefined && this.receipt.Weight !== null && this.receipt.Weight > 0) {
        return this.receipt.Weight;
      }
      return (this.localPackages || []).reduce((sum, p) => sum + this.getChargeableWeight(p), 0);
    },
    totalVolume() {
      return this.packages.reduce((sum, p) => sum + (parseFloat(p.Volume) || 0), 0);
    },
    weightCost() {
      if (this.isReadonlyReceipt && this.receipt.WeightCost !== undefined && this.receipt.WeightCost !== null && this.receipt.WeightCost > 0) {
        return this.receipt.WeightCost;
      }
      return (this.localPackages || []).reduce((sum, p) => sum + this.getPackageTotal(p), 0);
    },
    totalAmount() {
      if (this.receipt.Status === EXPORT_RECEIPT_STATUS.CANCELED) {
        return 0;
      }
      if (this.localPackages && this.localPackages.length > 0) {
        return calculateLiquidationAmount(this.localPackages);
      }
      return parseFloat(this.receipt.LiquidationAmount) || 0;
    },
    payableShipVN() {
      const isPaidShip = this.receipt && this.receipt.IsPaidShip == 1;
      if (isPaidShip) return 0;
      const shipPaymentType = Number(this.localShipPaymentType !== undefined && this.localShipPaymentType !== null ? this.localShipPaymentType : (this.receipt ? this.receipt.ShipPaymentType : 0));
      return shipPaymentType === 0 ? (parseFloat(this.shipVN) || 0) : 0;
    },
    walletAfterExport() {
      const wallet = parseFloat(this.customer.Wallet !== undefined && this.customer.Wallet !== null ? this.customer.Wallet : (this.receipt.Wallet || 0)) || 0;
      return wallet - this.totalAmount - this.payableShipVN;
    },
    isExportedReceipt() {
      return this.receipt.Status === EXPORT_RECEIPT_STATUS.EXPORTED;
    },
    isReadonlyReceipt() {
      if (!this.canOperate) {
        return true;
      }
      return [
        EXPORT_RECEIPT_STATUS.EXPORTED,
        EXPORT_RECEIPT_STATUS.CANCELED
      ].includes(this.receipt.Status);
    },
    canExportReceipt() {
      return this.canOperate && [
        EXPORT_RECEIPT_STATUS.PREPARING,
        EXPORT_RECEIPT_STATUS.WAITING_REDELIVERY,
        EXPORT_RECEIPT_STATUS.SHIPPING
      ].includes(this.receipt.Status);
    },
    canRedeliverReceipt() {
      return this.canOperate && this.receipt.Status === EXPORT_RECEIPT_STATUS.SHIPPING;
    },
    canShipReceipt() {
      return this.canOperate && [
        EXPORT_RECEIPT_STATUS.PREPARING,
        EXPORT_RECEIPT_STATUS.WAITING_REDELIVERY
      ].includes(this.receipt.Status);
    },
    groupedPackages() {
      const groups = {};
      this.localPackages.forEach(p => {
        const orderId = p.OrderID || 0;
        if (!groups[orderId]) {
          groups[orderId] = [];
        }
        groups[orderId].push(p);
      });
      
      return Object.keys(groups).map((orderId, index) => {
        const pkgs = groups[orderId];
        
        const subtotalWeight = pkgs.reduce((sum, p) => sum + (parseFloat(p.Weight) || 0), 0);
        const subtotalVolume = pkgs.reduce((sum, p) => sum + (parseFloat(p.Volume) || 0), 0);
        const subtotalFeeShip = pkgs.reduce((sum, p) => sum + this.getPackageTotal(p), 0);
        const subtotalFeeFragileGoods = pkgs.reduce((sum, p) => sum + (parseFloat(p.FeeFragileGoods) || 0), 0);
        const subtotalFeeOther = pkgs.reduce((sum, p) => sum + (parseFloat(p.FeeOther) || 0), 0);
        const subtotalProductAmount = pkgs.reduce((sum, p) => sum + (parseFloat(p.ProductAmount) || 0), 0);
        const subtotalLiquidationAmount = pkgs.reduce((sum, p) => sum + (parseFloat(p.LiquidationAmount) || 0), 0);
        const subtotalChargeableWeight = pkgs.reduce((sum, p) => sum + this.getChargeableWeight(p), 0);
        const subtotalWeightByVolume = pkgs.reduce((sum, p) => sum + (parseFloat(p.WeightByVolume) || 0), 0);
        const subtotalNumberOfPack = pkgs.reduce((sum, p) => sum + (parseInt(p.NumberOfPack) || 0), 0);
        
        return {
          orderId: orderId,
          packages: pkgs,
          stt: index + 1,
          rowspan: pkgs.length, // Only spans the package rows, not the subtotal row because the subtotal row renders separately
          subtotalWeight,
          subtotalVolume,
          subtotalFeeShip,
          subtotalFeeFragileGoods,
          subtotalFeeOther,
          subtotalProductAmount,
          subtotalLiquidationAmount,
          subtotalChargeableWeight,
          subtotalWeightByVolume,
          subtotalNumberOfPack
        };
      });
    },
    sumRawWeight() {
      return (this.localPackages || []).reduce((sum, p) => sum + (parseFloat(p.Weight) || 0), 0);
    },
    sumVolume() {
      return (this.localPackages || []).reduce((sum, p) => sum + (parseFloat(p.Volume) || 0), 0);
    },
    sumWeightByVolume() {
      return (this.localPackages || []).reduce((sum, p) => sum + (parseFloat(p.WeightByVolume) || 0), 0);
    },
    sumNumberOfPack() {
      return (this.localPackages || []).reduce((sum, p) => sum + (parseInt(p.NumberOfPack) || 0), 0);
    }
  },
  methods: {
    getChargeableWeight(p) {
      return getChargeableWeight(p);
    },
    display_ship_method(val) {
      if (val === null || val === undefined || val === '') return '';
      const method = _.find(SHIP_METHODS, item => item.name.toLowerCase() === val.toString().toLowerCase() || item.value.toString() === val.toString());
      return method ? method.name : val;
    },
    display_ship_payment_type(val) {
      const payment = _.find(SHIP_PAYMENT_TYPES, p => p.value.toString() === (val !== undefined && val !== null ? val.toString() : '0'));
      return payment ? payment.name : 'Trừ ví điện tử của khách';
    },
    getReceiptStatusName(status) {
      const item = _.find(configs.EXPORT_RECEIPT_STATUS, { value: status });
      return item ? item.name : (status || '');
    },
    getUnitPrice(p) {
      if (p.OrderFeeWeight !== undefined && p.OrderFeeWeight !== null && p.OrderFeeWeight > 0) {
        return p.OrderFeeWeight;
      }
      const w = this.getChargeableWeight(p);
      if (w === 0) return 0;
      return p.FeeShip ? (p.FeeShip / w) : 25000;
    },
    getPackageTotal(p) {
      return getPackageTotal(p);
    },
    updateMatchedCount() {
      const matched = this.localPackages.filter(p => p.Matched).length;
      this.$emit('update-matched', matched);
    },
    checkAllPackages() {
      this.localPackages.forEach(p => {
        p.Matched = true;
      });
      this.updateMatchedCount();
      this.playBeep(true);
    },
    handleHeaderScan() {
      const code = this.scanCodeHeader.trim().toLowerCase();
      if (!code) return;
      this.scanCode(code);
      this.scanCodeHeader = '';
    },
    handleBottomScan() {
      const code = this.scanCodeBottom.trim().toLowerCase();
      if (!code) return;
      this.scanCode(code);
      this.scanCodeBottom = '';
    },
    scanCode(code) {
      const foundPkg = this.localPackages.find(p => p.TransactionCode && p.TransactionCode.toLowerCase() === code);
      if (foundPkg) {
        if (!foundPkg.Matched) {
          foundPkg.Matched = true;
          this.updateMatchedCount();
        }
        this.playBeep(true);
      } else {
        this.playBeep(false);
      }
    },
    playBeep(success = true) {
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        if (success) {
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
          gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
          oscillator.start();
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
          oscillator.stop(audioCtx.currentTime + 0.15);
        } else {
          oscillator.type = 'sawtooth';
          oscillator.frequency.setValueAtTime(220, audioCtx.currentTime);
          gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
          oscillator.start();
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
          oscillator.stop(audioCtx.currentTime + 0.3);
        }
      } catch (e) {
        console.error(e);
      }
    },
    confirmDeletePackage(packages) {
      if (confirm(`Bạn có chắc muốn xóa các mã vận đơn của đơn hàng này khỏi phiếu xuất?`)) {
        packages.forEach(p => {
          this.$emit('delete-package', p.ID);
        });
      }
    },
    confirmDeleteSinglePackage(p) {
      if (confirm(`Bạn có chắc muốn xóa mã vận đơn ${p.TransactionCode} khỏi phiếu xuất?`)) {
        this.$emit('delete-package', p.ID);
      }
    },
    updateReceipt() {
      if (!this.validateShipFees()) {
        return;
      }
      if (!this.totalWeight || parseFloat(this.totalWeight) === 0) {
        if (!confirm('Bạn đang lưu phiếu xuất với tổng kg bằng 0. Bạn có chắc chắn?')) {
          return;
        }
      }
      this.$emit('update-receipt', this.buildReceiptPayload());
    },
    setCustomerShipVN(value) {
      this.shipVN = value;
    },
    setCompanyShipVN(value) {
      this.companyShipVN = value;
    },
    getShipFeeValues() {
      return {
        customer: this.shipVN === null || this.shipVN === undefined ? null : parseFloat(this.shipVN),
        company: this.companyShipVN === null || this.companyShipVN === undefined ? null : parseFloat(this.companyShipVN)
      };
    },
    validateShipFees() {
      const fees = this.getShipFeeValues();
      if ((fees.customer !== null && (isNaN(fees.customer) || fees.customer < 0))
        || (fees.company !== null && (isNaN(fees.company) || fees.company < 0))) {
        window.getApp.$emit('APP_RESOURCE_FAILED', 'Tiền ship hàng không được là số âm.');
        return false;
      }
      if (fees.customer === null && fees.company === null) {
        window.getApp.$emit('APP_RESOURCE_FAILED', 'Bắt buộc nhập ít nhất một mục tiền ship hàng: khách trả hoặc cty trả.');
        return false;
      }
      return true;
    },
    buildReceiptPayload() {
      const fees = this.getShipFeeValues();
      return {
        ID: this.receipt.ID,
        Receiver: this.localReceiver,
        Phone: this.localPhone,
        Address: this.localAddress,
        ShippingMethod: this.localShippingMethod,
        ShipPaymentType: this.localShipPaymentType,
        ShipVN: fees.customer === null ? '[null]' : fees.customer,
        CompanyShipVN: fees.company === null ? '[null]' : fees.company,
        LiquidationAmount: parseFloat(this.totalAmount) || 0,
        Weight: parseFloat(this.totalWeight) || 0,
        WeightCost: parseFloat(this.weightCost) || 0
      };
    },
    onExport() {
      if (!this.validateShipFees()) {
        return;
      }

      this.$emit('export', {
        ...this.buildReceiptPayload()
      });
    },
    shipReceipt() {
      if (!this.validateShipFees()) {
        return;
      }
      this.$emit('update-receipt', {
        ...this.buildReceiptPayload(),
        Status: EXPORT_RECEIPT_STATUS.SHIPPING
      });
    },
    redeliverReceipt() {
      if (!this.validateShipFees()) {
        return;
      }
      this.$emit('update-receipt', {
        ...this.buildReceiptPayload(),
        Status: EXPORT_RECEIPT_STATUS.WAITING_REDELIVERY
      });
    },
    formatCurrency(val) {
      return (val || 0).toLocaleString('vi-VN');
    },
    printReceipt() {
      const cssStyles = `
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-size: 13px;
          color: #333;
          margin: 0;
          padding: 20px;
          background-color: #fff;
        }
        .header { text-align: center; margin-bottom: 20px; }
        .header h1 { font-size: 24px; font-weight: 500; margin: 0; letter-spacing: 1px; }
        .meta-info { font-style: italic; font-size: 12px; margin-top: 5px; }
        .tag { display: inline-block; padding: 2px 6px; border-radius: 3px; color: #fff; font-size: 11px; font-style: normal; margin: 0 2px; font-weight: bold; }
        .tag-gray { background-color: #999; }
        .tag-red { background-color: #d9534f; }
        .tag-blue { background-color: #0059b3; }
        .top-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 25px; }
        .section-title { font-weight: bold; margin-bottom: 8px; font-size: 15px; }
        table { width: 100%; border-collapse: collapse; }
        .info-table td { border: 1px solid #ddd; padding: 8px; vertical-align: middle; }
        .label-bg { background-color: #f9f9f9; width: 30%; }
        .data-table th, .data-table td { border: 1px solid #ddd; padding: 8px; text-align: right; }
        .data-table th { background-color: #f9f9f9; font-weight: normal; text-align: left; }
        .data-table td.center { text-align: center; }
        .package-table { margin-top: 20px; font-size: 12px; }
        .package-table th { background-color: #f9f9f9; font-weight: 500; text-align: center; border: 1px solid #ddd; padding: 5px; }
        .package-table td { border: 1px solid #ddd; padding: 5px; text-align: center; }
        .text-blue { color: #337ab7; text-decoration: none; }
        .signature-row { margin-top: 30px; display: flex; justify-content: space-between; padding: 0 50px; font-weight: bold; }
        .signature-names { margin-top: 80px; display: flex; justify-content: space-between; padding: 0 50px; }
        .date-info { text-align: right; margin-top: 15px; font-size: 12px; }
        .subtotal-row td { background-color: #fafafa; }
      `;

      let rowsHTML = '';
      this.groupedPackages.forEach(group => {
        group.packages.forEach((p, idx) => {
          const isFirst = idx === 0;
          const sizeStr = (p.Length && p.Width && p.Height) ? `${p.Length} x ${p.Width} x ${p.Height}` : '';
          const weightCalculated = this.getChargeableWeight(p);

          rowsHTML += `<tr>`;
          if (isFirst) {
            rowsHTML += `<td rowspan="${group.rowspan}">${group.stt}</td>`;
          }
          rowsHTML += `
            <td><span class="text-blue">${p.TransactionCode || ''}</span></td>
            <td>${p.NumberOfPack || 0}</td>
            <td><span class="text-blue">${(p.PackageType === 1 ? 'KG' : 'MD') + p.OrderID}</span></td>
            <td>${p.Weight || 0} / ${p.Volume || 0} ${sizeStr ? `(${sizeStr})` : ''} / ${p.WeightByVolume || 0}</td>
            <td>${weightCalculated.toFixed(2)}</td>
            <td>${p.OrderNote || p.StoreVNNote || ''}</td>
          `;
          rowsHTML += `</tr>`;
        });

        rowsHTML += `
          <tr class="subtotal-row">
            <td colspan="2" style="text-align: right; border-top: none; font-weight: bold;">Tổng:</td>
            <td style="font-weight: bold;">${group.subtotalNumberOfPack}</td>
            <td></td>
            <td style="font-weight: bold;">
              ${group.subtotalWeight.toFixed(2)} KG /
              ${group.subtotalVolume.toFixed(2)} CBM /
              ${group.subtotalWeightByVolume.toFixed(2)} KG
            </td>
            <td style="font-weight: bold;">${group.subtotalChargeableWeight.toFixed(2)}</td>
            <td></td>
          </tr>
        `;
      });

      const statusName = this.getReceiptStatusName(this.receipt.Status);
      const paymentStatusName = this.walletAfterExport < 0 ? 'Chưa thanh toán' : 'Đủ số dư';

      const printHTML = `
        <!DOCTYPE html>
        <html lang="vi">
        <head>
          <meta charset="UTF-8">
          <title>Phiếu Xuất - PXK${this.receipt.ID}</title>
          <style>${cssStyles}</style>
        </head>
        <body>
          <div class="header">
            <h1>PHIẾU XUẤT</h1>
            <div class="meta-info">
              (Mã phiếu <strong>PXK${this.receipt.ID}</strong>) ${this.warehouseName}
              <span class="tag tag-gray">${statusName}</span> 
              <span class="tag ${this.walletAfterExport < 0 ? 'tag-red' : 'tag-blue'}">${paymentStatusName}</span>
            </div>
            <div style="margin-top: 5px;">Phương thức vận chuyển: ${this.localShippingMethod || ''}</div>
          </div>

          <div class="top-grid">
            <div>
              <div class="section-title">Khách hàng</div>
              <table class="info-table">
                <tr>
                  <td class="label-bg">Khách</td>
                  <td>${this.customerFullName}</td>
                </tr>
                <tr>
                  <td class="label-bg">Mã Khách</td>
                  <td>${this.customer.UserCode || this.receipt.UserCode || ''}</td>
                </tr>
                <tr>
                  <td class="label-bg">Địa chỉ</td>
                  <td style="font-size: 11px;">${this.localAddress || this.receipt.Address || this.customer.Address || ''}</td>
                </tr>
                <tr>
                  <td class="label-bg">Kiểu ship</td>
                  <td>${this.display_ship_method(this.localShippingMethod)}</td>
                </tr>
                <tr>
                  <td class="label-bg">Thu tiền ship</td>
                  <td>${this.display_ship_payment_type(this.localShipPaymentType)}</td>
                </tr>
              </table>
            </div>
            <div>
              <div class="section-title">Tiền cần thanh toán (đơn vị đ)</div>
              <table class="info-table">
                <tr>
                  <td class="label-bg">a. Tiền cần thanh toán</td>
                  <td style="text-align: right; font-weight: bold;">${this.formatCurrency(this.totalAmount)}</td>
                </tr>
                <tr>
                  <td class="label-bg">b. Tiền ship hàng (khách trả)</td>
                  <td style="text-align: right;">${this.formatCurrency(parseFloat(this.shipVN) || 0)}</td>
                </tr>
                <tr>
                  <td class="label-bg">c. Số dư hiện tại</td>
                  <td style="text-align: right;">${this.formatCurrency(this.customer.Wallet || 0)}</td>
                </tr>
                <tr>
                  <td class="label-bg"><strong>Số dư sau khi xuất kho (c - a - b)</strong></td>
                  <td style="text-align: right;"><strong>${this.formatCurrency(this.walletAfterExport)}</strong></td>
                </tr>
              </table>
            </div>
          </div>

          <div class="section-title">Thông tin phiếu xuất</div>
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 50%; text-align: center; font-weight: bold;">1. Tổng cân nặng/ Tổng số khối/Tổng cân quy đổi</th>
                <th style="width: 50%; text-align: center; font-weight: bold;">2. Tổng số kiện</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="text-align: center;">${this.sumRawWeight.toFixed(2)} KG / ${this.sumVolume.toFixed(2)} CBM / ${this.sumWeightByVolume.toFixed(2)} KG</td>
                <td style="text-align: center;">${this.sumNumberOfPack}</td>
              </tr>
            </tbody>
          </table>

          <div class="section-title" style="margin-top: 25px;">Thông tin gói hàng</div>
          <table class="package-table">
            <thead>
              <tr>
                <th>##</th>
                <th>Mã vận đơn</th>
                <th>Số kiện</th>
                <th>Đơn hàng</th>
                <th>Cân nặng/Số khối/Cân quy đổi</th>
                <th>Cân tính tiền</th>
                <th>Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHTML}
            </tbody>
          </table>

          <div class="date-info">Ngày <strong>${moment(this.receipt.CreatedDate).format('DD-MM-YYYY HH:mm:ss')}</strong></div>

          <div class="signature-row">
            <span>Khách hàng</span>
            <span>Nhân viên xuất kho</span>
          </div>
          <div class="signature-names">
            <span>${this.customerFullName}</span>
            <span>${this.receipt.CreatedByName || 'Kho NTG'}</span>
          </div>
        </body>
        </html>
      `;

      const frame = document.getElementById('print_frame');
      if (frame) {
        var doc = frame.contentWindow.document;
        doc.open();
        doc.write(printHTML);
        doc.close();
        setTimeout(function(){ frame.contentWindow.print(); }, 500);
      } else {
        const win = window.open("", "_blank");
        win.document.write(printHTML);
        win.document.close();
        setTimeout(function(){ win.print(); }, 500);
      }
    }
  }
};
</script>

<style scoped>
.export-receipt-detail {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 13px;
  color: #333;
  padding: 20px;
  background-color: #fff;
  min-height: 100%;
}

.header {
  text-align: center;
  margin-bottom: 20px;
}
.header h1 {
  font-size: 24px;
  font-weight: 500;
  margin: 0;
  letter-spacing: 1px;
}
.meta-info {
  font-style: italic;
  font-size: 12px;
  margin-top: 5px;
}
.tag {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  color: #fff;
  font-size: 11px;
  font-style: normal;
  margin: 0 2px;
  font-weight: bold;
}
.tag-gray { background-color: #999; }
.tag-red { background-color: #d9534f; }
.tag-blue { background-color: #337ab7; }

.top-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 25px;
}
.section-title {
  font-weight: bold;
  margin-bottom: 8px;
  font-size: 15px;
  border-bottom: 1px solid #eee;
  padding-bottom: 5px;
}
table {
  width: 100%;
  border-collapse: collapse;
}
.info-table td {
  border: 1px solid #ddd;
  padding: 8px;
  vertical-align: middle;
}
.label-bg {
  background-color: #f9f9f9;
  width: 30%;
  font-weight: 500;
}

.data-table th, .data-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: right;
}
.data-table th {
  background-color: #f9f9f9;
  font-weight: bold;
  text-align: left;
}
.data-table td.center { text-align: center; }

.package-table {
  margin-top: 20px;
  font-size: 12px;
}
.package-table th {
  background-color: #f9f9f9;
  font-weight: 500;
  text-align: center;
  border: 1px solid #ddd;
  padding: 5px;
}
.package-table td {
  border: 1px solid #ddd;
  padding: 5px;
  text-align: center;
}
.bill-checked td {
  background-color: rgba(92, 184, 92, 0.15) !important;
}
.text-blue { color: #337ab7; }

.btn-delete {
  background-color: #d9534f;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
}
.btn-delete:hover {
  background-color: #c9302c;
}

.input-mini {
  width: 80px;
  border: 1px solid #ccc;
  padding: 2px;
  font-size: 11px;
  border-radius: 3px;
}
.input-table {
  width: 100%;
  border: 1px solid #ccc;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 3px;
  box-sizing: border-box;
}
.input-inline {
  border: 1px solid #ccc;
  padding: 2px 6px;
  font-size: 12px;
  border-radius: 3px;
}
.text-end { text-align: right; }

.btn-green-sm {
  background-color: #5cb85c;
  color: white;
  border: none;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
}
.btn-green-sm:hover {
  background-color: #4cae4c;
}

.subtotal-row td {
  background-color: #fbfbfb;
  border-top: 1px solid #ddd;
}

.signature-row {
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
  padding: 0 50px;
  font-weight: bold;
}
.signature-names {
  margin-top: 80px;
  display: flex;
  justify-content: space-between;
  padding: 0 50px;
}
.date-info {
  text-align: right;
  margin-top: 15px;
  font-size: 12px;
}

.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  padding: 10px 20px;
  border-top: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
  z-index: 1000;
}
.search-box {
  display: flex;
  gap: 5px;
}
.search-box input {
  width: 300px;
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
}
.btn-action {
  padding: 6px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 5px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  background-color: #fff;
}
.btn-action:hover {
  background-color: #f5f5f5;
}
.btn-red { background-color: #d9534f; color: white; border: none; }
.btn-red:hover { background-color: #c9302c; }
.btn-green { background-color: #5cb85c; color: white; border: none; }
.btn-green:hover { background-color: #4cae4c; }
.btn-blue { background-color: #5bc0de; color: white; border: none; }
.btn-blue:hover { background-color: #46b8da; }
.btn-warning { background-color: #f0ad4e; color: white; border: none; }
.btn-warning:hover { background-color: #ec971f; }
.btn-dark-red { background-color: #c9302c; color: white; border: none; }
.btn-dark-red:hover { background-color: #ac2925; }

@media print {
  .no-print { display: none !important; }
  body { padding: 0; }
}

::v-deep .compact-currency { margin: 0; padding: 0; }
::v-deep .compact-currency .v-text-field__details { display: none !important; }
::v-deep .compact-currency .v-input__slot { margin-bottom: 0 !important; }
::v-deep .compact-currency .v-input__slot::before,
::v-deep .compact-currency .v-input__slot::after { display: none !important; }
::v-deep .compact-currency input {
  text-align: right !important;
  font-size: 12px !important;
  padding: 4px !important;
  border: 1px solid #ccc !important;
  width: 100%;
}
</style>
